import React from "react";
import {
  SafetyCertificateOutlined,
  UserOutlined,
  CalendarOutlined,
  TeamOutlined,
  BarChartOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";

import logo from "./exellentkidslogo.png";

/* ============================================================
   EXCELLENT KIDS FOUNDATION
   FORMAL PERIODIC GRADE SHEET
============================================================ */

const COLORS = {
  deepNavy: "#031326",
  navy: "#061A33",
  royalNavy: "#0A2E5A",
  blue: "#124A8A",

  charcoal: "#111827",
  slate: "#334155",

  gold: "#D4A017",
  brightGold: "#F4C542",
  softGold: "#FFF7D6",

  white: "#FFFFFF",
  paper: "#FFFFFF",
  soft: "#F5F7FA",
  softBlue: "#EDF4FB",

  text: "#0F172A",
  muted: "#64748B",
  border: "#CBD5E1",

  danger: "#B42318",
  dangerSoft: "#FFF1F0",

  success: "#1F8A5B",
  successSoft: "#ECFDF3",
};

const getNumericScore = (score) => {
  if (score === null || score === undefined || score === "") {
    return null;
  }

  const cleaned = String(score).replace("%", "").trim();
  const numeric = Number(cleaned);

  return Number.isFinite(numeric) ? numeric : null;
};

const getScoreClass = (score) => {
  const numeric = getNumericScore(score);

  if (numeric !== null) {
    if (numeric >= 90) return "high";
    if (numeric >= 70) return "normal";
    return "low";
  }

  const value = String(score || "").trim().toUpperCase();

  if (value === "D" || value === "F") {
    return "low";
  }

  return "normal";
};

const getAveragePercentage = (score) => {
  const numeric = getNumericScore(score);

  if (numeric === null) {
    return 0;
  }

  return Math.max(0, Math.min(100, numeric));
};

const ScreenGradeSheet = ({ data }) => {
  const subjects = Array.isArray(data?.student_subjects)
    ? data.student_subjects
    : [];

  const averagePercentage = getAveragePercentage(data?.average_score);

  const averageClass = getScoreClass(data?.average_score);

  const studentName = data?.student_name || "N/A";
  const gradeClass = data?.grade_class_name || "N/A";
  const periodName = data?.period_name || "N/A";
  const averageScore =
    data?.average_score !== null &&
    data?.average_score !== undefined &&
    data?.average_score !== ""
      ? data.average_score
      : "N/A";

  const rank =
    data?.rank !== null &&
    data?.rank !== undefined &&
    data?.rank !== ""
      ? data.rank
      : "N/A";

  const remark = data?.remark || "No remark available.";

  return (
    <>
      <style>{`
        /* ========================================================
           PAGE
        ======================================================== */

        .ek-grade-page {
          width: 100%;
          min-height: 100%;
          padding: 28px 18px 40px;
          background:
            linear-gradient(
              135deg,
              #eef2f6 0%,
              #f7f9fb 48%,
              #e9eef4 100%
            );
          box-sizing: border-box;
        }

        .ek-grade-document {
          width: 100%;
          max-width: 1060px;
          margin: 0 auto;
          background: ${COLORS.paper};
          border: 1px solid #b8c4d1;
          position: relative;
          overflow: hidden;

          box-shadow:
            0 16px 45px rgba(3, 19, 38, 0.12),
            0 3px 10px rgba(3, 19, 38, 0.06);

          box-sizing: border-box;
        }

        /*
          Formal inner document border.
        */
        .ek-grade-document::before {
          content: "";
          position: absolute;
          inset: 8px;
          border: 1px solid rgba(212, 160, 23, 0.45);
          pointer-events: none;
          z-index: 1;
        }

        .ek-grade-content {
          position: relative;
          z-index: 2;
        }

        /* ========================================================
           HEADER
        ======================================================== */

        .ek-grade-header {
          background:
            linear-gradient(
              115deg,
              ${COLORS.deepNavy} 0%,
              ${COLORS.navy} 52%,
              ${COLORS.royalNavy} 100%
            );
          color: #ffffff;
          padding: 34px 42px 0;
          position: relative;
        }

        .ek-grade-header-top {
          display: grid;
          grid-template-columns: 125px minmax(0, 1fr) 155px;
          align-items: center;
          gap: 24px;
        }

        .ek-grade-logo-box {
          width: 112px;
          height: 112px;
          background: #ffffff;
          border: 3px solid ${COLORS.gold};
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 7px;
          box-sizing: border-box;
          box-shadow: 0 7px 20px rgba(0, 0, 0, 0.22);
        }

        .ek-grade-logo {
          width: 100%;
          height: 100%;
          object-fit: contain;
          display: block;
        }

        .ek-grade-school-block {
          min-width: 0;
        }

        .ek-grade-school-name {
          margin: 0;
          font-size: clamp(25px, 3vw, 39px);
          line-height: 1.05;
          font-weight: 900;
          letter-spacing: -0.03em;
          color: #ffffff;
        }

        .ek-grade-report-title {
          margin-top: 10px;
          color: ${COLORS.brightGold};
          font-size: 14px;
          font-weight: 900;
          letter-spacing: 0.18em;
          text-transform: uppercase;
        }

        .ek-grade-address {
          margin: 10px 0 0;
          max-width: 700px;
          color: rgba(255, 255, 255, 0.84);
          font-size: 12px;
          line-height: 1.6;
        }

        .ek-grade-contact {
          margin: 3px 0 0;
          color: rgba(255, 255, 255, 0.76);
          font-size: 12px;
        }

        .ek-grade-official {
          min-height: 94px;
          border: 1px solid rgba(244, 197, 66, 0.7);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 12px;
          box-sizing: border-box;
          background: rgba(255, 255, 255, 0.045);
        }

        .ek-grade-official-icon {
          font-size: 29px;
          color: ${COLORS.brightGold};
          margin-bottom: 7px;
        }

        .ek-grade-official-label {
          color: #ffffff;
          font-size: 10px;
          font-weight: 900;
          letter-spacing: 0.13em;
          line-height: 1.45;
        }

        .ek-grade-header-rule {
          height: 7px;
          margin-top: 28px;
          background:
            linear-gradient(
              to right,
              ${COLORS.gold} 0%,
              ${COLORS.brightGold} 50%,
              ${COLORS.gold} 100%
            );
          position: relative;
        }

        .ek-grade-header-rule::after {
          content: "";
          position: absolute;
          left: 25%;
          right: 25%;
          top: 2px;
          height: 3px;
          background: #ffffff;
          opacity: 0.3;
        }

        /* ========================================================
           MAIN CONTENT
        ======================================================== */

        .ek-grade-main {
          padding: 28px 42px 34px;
        }

        .ek-grade-section {
          margin-bottom: 27px;
        }

        .ek-grade-section:last-child {
          margin-bottom: 0;
        }

        .ek-grade-section-heading {
          display: flex;
          align-items: center;
          gap: 10px;
          min-height: 38px;
          padding: 0 13px;
          background: ${COLORS.deepNavy};
          color: #ffffff;
          border-left: 5px solid ${COLORS.gold};
          box-sizing: border-box;
        }

        .ek-grade-section-heading-icon {
          color: ${COLORS.brightGold};
          font-size: 15px;
          flex: 0 0 auto;
        }

        .ek-grade-section-heading-text {
          font-size: 11px;
          font-weight: 900;
          letter-spacing: 0.13em;
          text-transform: uppercase;
        }

        /* ========================================================
           STUDENT INFORMATION
        ======================================================== */

        .ek-grade-info-table {
          width: 100%;
          border-collapse: collapse;
          table-layout: fixed;
        }

        .ek-grade-info-table td {
          border: 1px solid ${COLORS.border};
          padding: 13px 15px;
          vertical-align: middle;
          box-sizing: border-box;
        }

        .ek-grade-info-label {
          width: 18%;
          background: #eef2f6;
          color: ${COLORS.slate};
          font-size: 10px;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .ek-grade-info-value {
          width: 32%;
          background: #ffffff;
          color: ${COLORS.text};
          font-size: 14px;
          font-weight: 750;
          overflow-wrap: anywhere;
          word-break: break-word;
        }

        .ek-grade-info-icon {
          margin-right: 7px;
          color: ${COLORS.royalNavy};
        }

        /* ========================================================
           SUBJECT TABLE
        ======================================================== */

        .ek-grade-table-wrap {
          width: 100%;
          overflow: visible;
        }

        .ek-grade-table {
          width: 100%;
          border-collapse: collapse;
          table-layout: fixed;
          background: #ffffff;
        }

        .ek-grade-table th,
        .ek-grade-table td {
          border: 1px solid ${COLORS.border};
          vertical-align: middle;
          box-sizing: border-box;
        }

        .ek-grade-table thead th {
          background: ${COLORS.deepNavy};
          color: #ffffff;
          padding: 12px 14px;
          font-size: 11px;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          border-color: ${COLORS.deepNavy};
        }

        .ek-grade-table thead tr {
          border-bottom: 3px solid ${COLORS.gold};
        }

        .ek-grade-table tbody td {
          padding: 12px 14px;
          font-size: 13px;
          color: ${COLORS.text};
          background: #ffffff;
        }

        .ek-grade-table tbody tr:nth-child(even) td {
          background: #f8fafc;
        }

        .ek-grade-table tbody tr:hover td {
          background: #f4f7fb;
        }

        .ek-col-number {
          width: 60px;
          text-align: center;
        }

        .ek-col-subject {
          width: auto;
          text-align: left;
        }

        .ek-col-score {
          width: 150px;
          text-align: center;
        }

        .ek-number {
          color: ${COLORS.muted};
          font-weight: 800;
        }

        .ek-subject-name {
          color: ${COLORS.text};
          font-weight: 750;
          line-height: 1.45;
          overflow-wrap: anywhere;
          word-break: break-word;
        }

        .ek-grade-score {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 78px;
          min-height: 32px;
          padding: 5px 10px;
          border: 1px solid;
          border-radius: 3px;
          font-size: 13px;
          font-weight: 900;
          line-height: 1;
          box-sizing: border-box;
        }

        .ek-grade-score.high {
          color: #745600;
          background: ${COLORS.softGold};
          border-color: ${COLORS.gold};
        }

        .ek-grade-score.normal {
          color: ${COLORS.royalNavy};
          background: ${COLORS.softBlue};
          border-color: #9bb9d8;
        }

        .ek-grade-score.low {
          color: ${COLORS.danger};
          background: ${COLORS.dangerSoft};
          border-color: #e8b2ad;
        }

        .ek-no-results {
          padding: 28px 15px !important;
          text-align: center;
          color: ${COLORS.muted} !important;
          font-style: italic;
        }

        /* ========================================================
           PERFORMANCE SUMMARY
        ======================================================== */

        .ek-grade-summary-table {
          width: 100%;
          border-collapse: collapse;
          table-layout: fixed;
        }

        .ek-grade-summary-table th,
        .ek-grade-summary-table td {
          border: 1px solid ${COLORS.border};
          box-sizing: border-box;
        }

        .ek-grade-summary-table th {
          width: 19%;
          padding: 13px 15px;
          background: #eef2f6;
          color: ${COLORS.slate};
          text-align: left;
          font-size: 10px;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .ek-grade-summary-table td {
          padding: 12px 15px;
          color: ${COLORS.text};
          background: #ffffff;
          font-size: 14px;
          font-weight: 750;
        }

        .ek-average-cell {
          width: 31%;
        }

        .ek-rank-cell {
          width: 31%;
        }

        .ek-average-value {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 20px;
          font-weight: 900;
          color: ${COLORS.royalNavy};
        }

        .ek-average-value.high {
          color: #8a6700;
        }

        .ek-average-value.low {
          color: ${COLORS.danger};
        }

        .ek-average-icon {
          color: ${COLORS.gold};
          font-size: 18px;
        }

        .ek-average-bar {
          width: 100%;
          max-width: 220px;
          height: 5px;
          margin-top: 7px;
          background: #e5e7eb;
          overflow: hidden;
        }

        .ek-average-bar span {
          display: block;
          height: 100%;
          background:
            linear-gradient(
              to right,
              ${COLORS.royalNavy},
              ${COLORS.gold}
            );
        }

        .ek-rank-value {
          font-size: 18px;
          font-weight: 900;
          color: ${COLORS.royalNavy};
        }

        .ek-remark {
          line-height: 1.55;
        }

        /* ========================================================
           SIGNATURES
        ======================================================== */

        .ek-grade-signatures {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 70px;
          padding: 22px 10px 5px;
        }

        .ek-grade-signature {
          text-align: center;
          min-width: 0;
        }

        .ek-grade-signature-space {
          height: 42px;
        }

        .ek-grade-signature-line {
          border-top: 1px solid ${COLORS.charcoal};
          margin: 0 auto 7px;
          width: 100%;
        }

        .ek-grade-signature-title {
          color: ${COLORS.text};
          font-size: 11px;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .ek-grade-signature-subtitle {
          margin-top: 3px;
          color: ${COLORS.muted};
          font-size: 9px;
        }

        /* ========================================================
           FOOTER
        ======================================================== */

        .ek-grade-footer {
          border-top: 1px solid ${COLORS.border};
          margin-top: 25px;
          padding: 15px 42px 19px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 15px;
          color: ${COLORS.muted};
          font-size: 9px;
          letter-spacing: 0.04em;
          box-sizing: border-box;
        }

        .ek-grade-footer-brand {
          color: ${COLORS.royalNavy};
          font-weight: 800;
        }

        .ek-grade-footer-official {
          display: flex;
          align-items: center;
          gap: 5px;
          font-weight: 700;
        }

        .ek-grade-footer-official svg {
          color: ${COLORS.gold};
        }

        /* ========================================================
           TABLET
        ======================================================== */

        @media (max-width: 850px) {
          .ek-grade-page {
            padding: 18px 10px 30px;
          }

          .ek-grade-header {
            padding: 28px 30px 0;
          }

          .ek-grade-main {
            padding: 24px 30px 30px;
          }

          .ek-grade-header-top {
            grid-template-columns: 100px minmax(0, 1fr);
            gap: 20px;
          }

          .ek-grade-logo-box {
            width: 92px;
            height: 92px;
          }

          .ek-grade-official {
            grid-column: 1 / -1;
            min-height: 48px;
            flex-direction: row;
            gap: 8px;
            justify-self: stretch;
          }

          .ek-grade-official-icon {
            margin-bottom: 0;
            font-size: 21px;
          }

          .ek-grade-info-label {
            width: 20%;
          }

          .ek-grade-info-value {
            width: 30%;
          }

          .ek-grade-footer {
            padding-left: 30px;
            padding-right: 30px;
          }
        }

        /* ========================================================
           MOBILE
        ======================================================== */

        @media (max-width: 600px) {
          .ek-grade-page {
            padding: 0;
            background: #ffffff;
          }

          .ek-grade-document {
            max-width: none;
            border: 0;
            box-shadow: none;
          }

          .ek-grade-document::before {
            inset: 4px;
            border-color: rgba(212, 160, 23, 0.35);
          }

          .ek-grade-header {
            padding: 25px 18px 0;
          }

          .ek-grade-header-top {
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            gap: 14px;
          }

          .ek-grade-logo-box {
            width: 82px;
            height: 82px;
          }

          .ek-grade-school-name {
            font-size: 25px;
            line-height: 1.1;
          }

          .ek-grade-report-title {
            margin-top: 7px;
            font-size: 10px;
            letter-spacing: 0.12em;
          }

          .ek-grade-address {
            margin-top: 8px;
            font-size: 10px;
            line-height: 1.5;
          }

          .ek-grade-contact {
            font-size: 10px;
          }

          .ek-grade-official {
            width: 100%;
            min-height: 42px;
            padding: 7px 10px;
          }

          .ek-grade-official-label {
            font-size: 9px;
          }

          .ek-grade-header-rule {
            height: 5px;
            margin-top: 20px;
          }

          .ek-grade-main {
            padding: 20px 12px 25px;
          }

          .ek-grade-section {
            margin-bottom: 21px;
          }

          .ek-grade-section-heading {
            min-height: 34px;
            padding: 0 9px;
            border-left-width: 4px;
          }

          .ek-grade-section-heading-icon {
            font-size: 13px;
          }

          .ek-grade-section-heading-text {
            font-size: 9px;
            letter-spacing: 0.09em;
          }

          /* ------------------------------------------------------
             MOBILE INFORMATION TABLE
          ------------------------------------------------------ */

          .ek-grade-info-table {
            table-layout: auto;
          }

          .ek-grade-info-table tr {
            display: grid;
            grid-template-columns: 39% 61%;
          }

          .ek-grade-info-table td {
            width: auto !important;
            min-width: 0;
            padding: 9px 8px;
          }

          .ek-grade-info-label {
            font-size: 8px;
            letter-spacing: 0.025em;
          }

          .ek-grade-info-value {
            font-size: 11px;
            line-height: 1.35;
          }

          .ek-grade-info-icon {
            margin-right: 4px;
          }

          /* ------------------------------------------------------
             MOBILE SUBJECT TABLE
             IMPORTANT: NO HORIZONTAL SCROLL
          ------------------------------------------------------ */

          .ek-grade-table-wrap {
            width: 100%;
            overflow: visible;
          }

          .ek-grade-table {
            width: 100%;
            table-layout: fixed;
          }

          .ek-grade-table thead th {
            padding: 9px 5px;
            font-size: 8px;
            letter-spacing: 0.015em;
            white-space: normal;
            line-height: 1.2;
          }

          .ek-grade-table tbody td {
            padding: 9px 5px;
            font-size: 11px;
          }

          .ek-col-number {
            width: 34px;
          }

          .ek-col-subject {
            width: auto;
            min-width: 0;
          }

          .ek-col-score {
            width: 76px;
          }

          .ek-number {
            font-size: 10px;
          }

          .ek-subject-name {
            font-size: 11px;
            line-height: 1.35;
            white-space: normal;
            overflow-wrap: anywhere;
            word-break: break-word;
          }

          .ek-grade-score {
            min-width: 52px;
            min-height: 27px;
            padding: 4px 5px;
            font-size: 10px;
          }

          /* ------------------------------------------------------
             MOBILE SUMMARY
          ------------------------------------------------------ */

          .ek-grade-summary-table {
            table-layout: fixed;
          }

          .ek-grade-summary-table tr {
            display: grid;
            grid-template-columns: 40% 60%;
          }

          .ek-grade-summary-table th,
          .ek-grade-summary-table td {
            width: auto !important;
            min-width: 0;
          }

          .ek-grade-summary-table th {
            padding: 9px 8px;
            font-size: 8px;
            letter-spacing: 0.02em;
          }

          .ek-grade-summary-table td {
            padding: 9px 8px;
            font-size: 11px;
          }

          .ek-average-value {
            font-size: 15px;
            gap: 6px;
          }

          .ek-average-icon {
            font-size: 14px;
          }

          .ek-average-bar {
            max-width: 100%;
            height: 4px;
          }

          .ek-rank-value {
            font-size: 14px;
          }

          .ek-remark {
            line-height: 1.4;
          }

          /* ------------------------------------------------------
             MOBILE SIGNATURES
          ------------------------------------------------------ */

          .ek-grade-signatures {
            grid-template-columns: 1fr;
            gap: 25px;
            padding: 17px 10px 0;
          }

          .ek-grade-signature-space {
            height: 30px;
          }

          .ek-grade-signature-title {
            font-size: 9px;
          }

          .ek-grade-signature-subtitle {
            font-size: 8px;
          }

          /* ------------------------------------------------------
             MOBILE FOOTER
          ------------------------------------------------------ */

          .ek-grade-footer {
            margin-top: 18px;
            padding: 12px 15px 16px;
            flex-direction: column;
            text-align: center;
            gap: 6px;
            font-size: 8px;
          }
        }

        /* ========================================================
           SMALL PHONES
        ======================================================== */

        @media (max-width: 380px) {
          .ek-grade-header {
            padding-left: 14px;
            padding-right: 14px;
          }

          .ek-grade-main {
            padding-left: 8px;
            padding-right: 8px;
          }

          .ek-grade-school-name {
            font-size: 22px;
          }

          .ek-grade-table thead th {
            padding: 8px 3px;
            font-size: 7px;
          }

          .ek-grade-table tbody td {
            padding: 8px 3px;
          }

          .ek-col-number {
            width: 29px;
          }

          .ek-col-score {
            width: 67px;
          }

          .ek-subject-name {
            font-size: 10px;
          }

          .ek-grade-score {
            min-width: 48px;
            min-height: 25px;
            padding: 3px 4px;
            font-size: 9px;
          }

          .ek-grade-info-table td,
          .ek-grade-summary-table th,
          .ek-grade-summary-table td {
            padding: 8px 6px;
          }
        }

        /* ========================================================
           PRINT
        ======================================================== */

        @page {
          size: A4;
          margin: 10mm;
        }

        @media print {
          .ek-grade-page {
            padding: 0;
            background: #ffffff !important;
          }

          .ek-grade-document {
            max-width: none;
            border: 1px solid #111827;
            box-shadow: none;
          }

          .ek-grade-document::before {
            display: none;
          }

          .ek-grade-header {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          .ek-grade-section {
            break-inside: avoid;
          }

          .ek-grade-table {
            break-inside: auto;
          }

          .ek-grade-table tr {
            break-inside: avoid;
          }

          .ek-grade-footer {
            break-inside: avoid;
          }
        }
      `}</style>

      <div className="ek-grade-page">
        <div id="gradesheet" className="ek-grade-document">
          <div className="ek-grade-content">

            {/* ====================================================
                HEADER
            ==================================================== */}

            <header className="ek-grade-header">
              <div className="ek-grade-header-top">

                <div className="ek-grade-logo-box">
                  <img
                    src={logo}
                    alt="Excellent Kids Foundation"
                    className="ek-grade-logo"
                  />
                </div>

                <div className="ek-grade-school-block">
                  <h1 className="ek-grade-school-name">
                    Excellent Kids Foundation
                  </h1>

                  <div className="ek-grade-report-title">
                    Student Periodic Report
                  </div>

                  <p className="ek-grade-address">
                    GreenLand Community, Upper Johnsonville Township -
                    Left Bank 2B, Montserrado County, Liberia
                  </p>

                  <p className="ek-grade-contact">
                    Contact: +231-770-216-549
                  </p>
                </div>

                <div className="ek-grade-official">
                  <SafetyCertificateOutlined
                    className="ek-grade-official-icon"
                  />

                  <span className="ek-grade-official-label">
                    OFFICIAL
                    <br />
                    ACADEMIC REPORT
                  </span>
                </div>
              </div>

              <div className="ek-grade-header-rule" />
            </header>

            {/* ====================================================
                MAIN
            ==================================================== */}

            <main className="ek-grade-main">

              {/* ==================================================
                  STUDENT INFORMATION
              ================================================== */}

              <section className="ek-grade-section">
                <div className="ek-grade-section-heading">
                  <UserOutlined className="ek-grade-section-heading-icon" />

                  <span className="ek-grade-section-heading-text">
                    Student Information
                  </span>
                </div>

                <table className="ek-grade-info-table">
                  <tbody>
                    <tr>
                      <td className="ek-grade-info-label">
                        <UserOutlined className="ek-grade-info-icon" />
                        Student Name
                      </td>

                      <td className="ek-grade-info-value">
                        {studentName}
                      </td>

                      <td className="ek-grade-info-label">
                        <TeamOutlined className="ek-grade-info-icon" />
                        Grade Class
                      </td>

                      <td className="ek-grade-info-value">
                        {gradeClass}
                      </td>
                    </tr>

                    <tr>
                      <td className="ek-grade-info-label">
                        <CalendarOutlined className="ek-grade-info-icon" />
                        Reporting Period
                      </td>

                      <td className="ek-grade-info-value">
                        {periodName}
                      </td>

                      <td className="ek-grade-info-label">
                        <FileTextOutlined className="ek-grade-info-icon" />
                        Report Type
                      </td>

                      <td className="ek-grade-info-value">
                        Periodic Academic Report
                      </td>
                    </tr>
                  </tbody>
                </table>
              </section>

              {/* ==================================================
                  ACADEMIC PERFORMANCE
              ================================================== */}

              <section className="ek-grade-section">
                <div className="ek-grade-section-heading">
                  <BarChartOutlined className="ek-grade-section-heading-icon" />

                  <span className="ek-grade-section-heading-text">
                    Academic Performance
                  </span>
                </div>

                <div className="ek-grade-table-wrap">
                  <table className="ek-grade-table">
                    <thead>
                      <tr>
                        <th className="ek-col-number">
                          No.
                        </th>

                        <th className="ek-col-subject">
                          Course / Subject
                        </th>

                        <th className="ek-col-score">
                          Final Score
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {subjects.length > 0 ? (
                        subjects.map((subject, index) => {
                          const scoreClass = getScoreClass(
                            subject?.formatted_final_score
                          );

                          return (
                            <tr
                              key={`${subject?.subject_name || "subject"}-${index}`}
                            >
                              <td className="ek-col-number">
                                <span className="ek-number">
                                  {index + 1}
                                </span>
                              </td>

                              <td className="ek-col-subject">
                                <div className="ek-subject-name">
                                  {subject?.subject_name ||
                                    "Unnamed Course"}
                                </div>
                              </td>

                              <td className="ek-col-score">
                                <span
                                  className={`ek-grade-score ${scoreClass}`}
                                >
                                  {subject?.formatted_final_score ??
                                    "N/A"}
                                </span>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td
                            colSpan="3"
                            className="ek-no-results"
                          >
                            No academic results available.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* ==================================================
                  PERFORMANCE SUMMARY
              ================================================== */}

              <section className="ek-grade-section">
                <div className="ek-grade-section-heading">
                  <CheckCircleOutlined className="ek-grade-section-heading-icon" />

                  <span className="ek-grade-section-heading-text">
                    Performance Summary
                  </span>
                </div>

                <table className="ek-grade-summary-table">
                  <tbody>
                    <tr>
                      <th>
                        Average Score
                      </th>

                      <td className="ek-average-cell">
                        <div
                          className={`ek-average-value ${averageClass}`}
                        >
                          <BarChartOutlined className="ek-average-icon" />

                          <span>
                            {averageScore}
                          </span>
                        </div>

                        {averagePercentage > 0 && (
                          <div className="ek-average-bar">
                            <span
                              style={{
                                width: `${averagePercentage}%`,
                              }}
                            />
                          </div>
                        )}
                      </td>

                      <th>
                        Class Rank
                      </th>

                      <td className="ek-rank-cell">
                        <span className="ek-rank-value">
                          {rank}
                        </span>
                      </td>
                    </tr>

                    <tr>
                      <th>
                        Overall Remark
                      </th>

                      <td
                        colSpan="3"
                        className="ek-remark"
                      >
                        {remark}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </section>

              {/* ==================================================
                  AUTHORIZATION
              ================================================== */}

              <section className="ek-grade-section">
                <div className="ek-grade-section-heading">
                  <SafetyCertificateOutlined className="ek-grade-section-heading-icon" />

                  <span className="ek-grade-section-heading-text">
                    Authorization
                  </span>
                </div>

                <div className="ek-grade-signatures">
                  <div className="ek-grade-signature">
                    <div className="ek-grade-signature-space" />

                    <div className="ek-grade-signature-line" />

                    <div className="ek-grade-signature-title">
                      Class Teacher / Facilitator
                    </div>

                    <div className="ek-grade-signature-subtitle">
                      Signature &amp; Date
                    </div>
                  </div>

                  <div className="ek-grade-signature">
                    <div className="ek-grade-signature-space" />

                    <div className="ek-grade-signature-line" />

                    <div className="ek-grade-signature-title">
                      School Administration
                    </div>

                    <div className="ek-grade-signature-subtitle">
                      Signature &amp; Date
                    </div>
                  </div>
                </div>
              </section>
            </main>

            {/* ====================================================
                FOOTER
            ==================================================== */}

            <footer className="ek-grade-footer">
              <div className="ek-grade-footer-brand">
                Excellent Kids Foundation
              </div>

              <div className="ek-grade-footer-official">
                <SafetyCertificateOutlined />
                Official Student Academic Record
              </div>

              <div>
                Powered by Polu Integrated School Management System
              </div>
            </footer>

          </div>
        </div>
      </div>
    </>
  );
};

export default ScreenGradeSheet;